<template>
  <div
    v-if="showSeatTracker"
    class="seat-tracker">
    <div class="seat-tracker-top">
      {{ $t("message.seatCount") }}
    </div>
    <div
      class="seat-tracker-bar"
      :style="`background: ${getGradient()};`">
      <div class="needed-to-win-line" />
    </div>
    <div class="seat-tracker-bottom">
      <div class="dem-value">
        {{ demSeats.length }}
      </div>
      <div class="gop-value">
        {{ gopSeats.length }}
      </div>
    </div>
  </div>
  <div
    :id="id"
    class="state-leg-map" />
</template>

<script>
import { mapGetters } from "vuex";
import d3 from "@/plugins/d3";

import MapMixin from "@/mixins/map";
import { debounce } from "@/helpers/utils";

const mixins = [MapMixin];

const props = {
  results: {
    type: Array,
    required: true,
  },
  selectedResult: {
    type: Object,
    required: true,
  },
  district: {
    type: Number,
    required: true,
  },
  districtType: {
    type: String,
    required: true,
  },
};
const computed = {
  ...mapGetters(["colors"]),
  id() {
    return `state-leg-map`;
  },
  geojson() {
    return this.topojson(this.districtType, this.state)();
  },
  projection() {
    return this.stateFips && this.stateFips !== "00" && this.stateFips !== "15"
      ? d3.geoMercator()
      : d3.geoAlbersUsa();
  },
  propsKey() {
    const key = {
      cd: "CD116FP",
      sldl: "SLDLST",
      sldu: "SLDUST",
    };
    return key[this.districtType];
  },
  state() {
    return this.selectedResult.state_name;
  },
  calledSeats() {
    const winners = this.results.map((c) => c.candidates.filter((c) => c.winner));
    return [].concat.apply([], winners);
  },
  demSeats() {
    return this.calledSeats.filter((c) => c.party_name === "Democratic");
  },
  gopSeats() {
    return this.calledSeats.filter((c) => c.party_name === "Republican");
  },
  totalSeats() {
    if (
      this.results[0].office === "General Assembly" &&
      this.results[0].state_name === "New Jersey"
    ) {
      return this.results.length * 2;
    }
    return this.results.length;
  },
  showSeatTracker() {
    const isPrimary = this.selectedResult["election_type_id"] === 2;
    if (isPrimary) return false;

    return !this.selectedResult.party;
  },
};
const methods = {
  cleanDistrict(district) {
    return district.trim();
  },
  getSelectedResults(d) {
    const result = this.results.find(
      (item) =>
        this.cleanDistrict(item.district) === d.properties[this.propsKey].trim().replace(/^0+/, "")
    );
    return result;
  },
  getFillColor(d) {
    const resultData = this.getSelectedResults(d);
    if (!resultData) {
      return "#ccc";
    }
    let winner = resultData.candidates.find((c) => c.winning || c.winner);
    if (winner) {
      return winner.color;
    }
    return "#bdbdbd";
  },
  pathClick(feature) {
    const result = this.getSelectedResults(feature);
    const resultIndex = this.results.indexOf(result);

    if (resultIndex !== -1) {
      this.setSelectedResultIndex(resultIndex);
    }

    if (self.smAndDown) {
      self.highlightSelected(feature.properties.NAME, this, self.propsKey);
    }
  },
  selectionHighlighting() {
    const self = this;
    self.g.selectAll("path").each(function (d) {
      if (parseInt(d.properties[self.propsKey]) === parseInt(self.selectedResult.district)) {
        self.highlightSelected(d.properties[self.propsKey], this, self.propsKey);
      }
    });
  },
  getGradient() {
    const demColor = this.colors.party.Democratic;
    const gopColor = this.colors.party.Republican;

    const demPerc = (this.demSeats.length / this.totalSeats) * 100;
    const gopPerc = (this.gopSeats.length / this.totalSeats) * 100;
    const remaining = 100 - demPerc - gopPerc;
    return `linear-gradient(to right, ${demColor} 0%, ${demColor} ${demPerc}%, #E0E0E0 ${demPerc}%, #E0E0E0 ${demPerc + remaining}%, ${gopColor} ${demPerc + remaining}%, ${gopColor} 100%)`;
  },
};

const watch = {
  results(value, oldValue) {
    if (value.lastUpdated !== oldValue.lastUpdated) {
      this.drawMap();
    }
  },
  selectedResult() {
    this.drawMap();
  },
};

export default {
  mixins,
  props,
  data() {
    return {
      config: {
        disableSvgClick: true,
        style: {
          opacity: 0.9,
          strokeWidth: () => "0.5px",
        },
      },
      g: null,
    };
  },
  computed,
  watch,
  mounted() {
    this.drawMap();

    window.addEventListener(
      "resize",
      debounce(() => {
        this.drawMap();
      }),
      1000
    );
  },
  beforeUnmount() {
    window.removeEventListener(
      "resize",
      debounce(() => {
        this.drawMap();
      }),
      1000
    );

    this.hideTooltip();
  },
  methods,
};
</script>

<style>
.state-leg-map {
  display: flex;
  align-items: center;
}

.seat-tracker {
  display: block;
  width: 160px;
  height: 40px;
  position: relative;
  text-align: center;
  margin: 0 auto 20px;
}

.seat-tracker-bar {
  position: relative;
  height: 10px;
  top: 0;
  z-index: 1;
}

.needed-to-win-line {
  position: absolute;
  top: 0;
  left: calc(50% - 0.5px);
  bottom: 20px;
  transform: translateX(50%);
  border-left: 0.5px solid #666;
  height: 10px;
  z-index: 2;
}

.seat-tracker-top {
  font-size: 13px;
  font-weight: 400;
  text-align: left;
}

.seat-tracker-bottom {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 300;
  bottom: 0;
}

.dem-value {
  color: #4575a1;
  font-weight: 600;
}

.gop-value {
  color: #d84b44;
  font-weight: 600;
  text-align: right;
}
</style>
